import { buildEmploymentFormData } from './utils/formDataEmployment.js';

let hasSubmitted = false;

export async function handleFormSubmit(formElement, endpoint, prebuiltFormData = null) {
  if (hasSubmitted) {
    // console.warn('[Form Submission] Duplicate submission attempt prevented');
    return;
  }
  hasSubmitted = true;

  const getRecaptchaToken = () => {
    return new Promise((resolve, reject) => {
      const tokenInput = formElement.querySelector('#recaptchaToken');
      if (tokenInput?.value) {
        // console.log('[Form Submission] Existing reCAPTCHA token found');
        return resolve(tokenInput.value);
      }

      if (!tokenInput) {
        return reject(new Error('[Form Submission] reCAPTCHA input not found'));
      }

      const timeout = setTimeout(() => {
        observer.disconnect();
        const error = new Error('[Form Submission] reCAPTCHA token timeout');
        error.isRecaptchaTimeout = true;
        reject(error);
      }, 8000);

      const observer = new MutationObserver(() => {
        const newToken = tokenInput?.value;
        if (newToken) {
          // console.log('[Form Submission] reCAPTCHA token set via observer');
          clearTimeout(timeout);
          observer.disconnect();
          resolve(newToken);
        }
      });

      observer.observe(tokenInput, { attributes: true, attributeFilter: ['value'] });
      // console.log('[Form Submission] Waiting for reCAPTCHA token to be set');
    });
  };

  const existingErrors = formElement.querySelectorAll('.error-message');
  existingErrors.forEach(error => error.textContent = '');

  const formErrorMessage = formElement.querySelector('.form-error-message');
  if (formErrorMessage) {
    formErrorMessage.remove();
  }

  const buttons = formElement.querySelectorAll('button, input[type="submit"]');
  const inputs = formElement.querySelectorAll('input, textarea, select');
  buttons.forEach(button => {
    button.disabled = true;
    button.classList.add('submitting');
  });
  inputs.forEach(input => input.disabled = true);

  let originalText = '';
  const submitButton = formElement.querySelector('button[type="submit"]');
  if (submitButton) {
    originalText = submitButton.textContent;
    submitButton.textContent = 'Submitting...';
  }

  const resetFormState = () => {
    hasSubmitted = false;
    buttons.forEach(button => {
      button.disabled = false;
      button.classList.remove('submitting');
    });
    inputs.forEach(input => input.disabled = false);
    if (submitButton) {
      submitButton.textContent = originalText;
    }
  };

  const handleResponse = async (response) => {
    const contentType = response.headers.get('content-type');
    const rawText = await response.text();

    if (!response.ok) {
      let errorData = {};
      try {
        if (contentType && (contentType.includes('application/json') || contentType.includes('application/problem+json'))) {
          errorData = JSON.parse(rawText);
        } else {
          errorData = { message: 'Non-JSON response received', rawText };
        }
      } catch (e) {
        errorData = { message: 'Invalid JSON or no body returned', rawText };
      }
      throw { status: response.status, errorData };
    }

    try {
      return JSON.parse(rawText);
    } catch (e) {
      return {};
    }
  };

  try {
    await getRecaptchaToken();
    // console.log('[Form Submission] reCAPTCHA token available, proceeding');

    if (endpoint === 'careers/apply') {
      const formData = prebuiltFormData || buildEmploymentFormData(formElement);
      // console.log('[Form Submission] Employment form data built');

      const response = await fetch(`/api/careers/apply`, {
        method: 'POST',
        body: formData
      });

      await handleResponse(response);
      formElement.style.display = 'none';
      const successMessage = document.querySelector('.form-submission-success');
      if (successMessage) successMessage.style.display = 'block';
    } else {
      const jsonObject = {};
      const formFields = formElement.querySelectorAll('input, textarea, select');
      formFields.forEach(field => {
        if (field.name) {
          if (field.type === 'checkbox') {
            jsonObject[field.name] = field.checked;
          } else if (field.type === 'select-multiple') {
            jsonObject[field.name] = Array.from(field.selectedOptions).map(option => option.value);
          } else {
            jsonObject[field.name] = field.value;
          }
        }
      });

      // console.log('[Form Submission] JSON form data:', jsonObject);

      const response = await fetch(`/api/enquiries/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(jsonObject)
      });

      await handleResponse(response);

      const formControl = document.getElementById('formControl');
      const formSelection = document.querySelector('.form-selection');
      const allForms = document.querySelectorAll('.contact-form-partial');
      if (formControl) formControl.style.display = 'none';
      if (formSelection) formSelection.style.display = 'none';
      allForms.forEach(form => form.style.display = 'none');

      const successMessage = document.querySelector('.form-submission-success');
      if (successMessage) successMessage.style.display = 'block';
    }
  } catch (error) {
    console.error('[Form Submission] Error occurred:', error);
    const errorMessage = document.createElement('div');
    errorMessage.className = 'form-error-message';
    if (error.isRecaptchaTimeout) {
      errorMessage.textContent = 'Something went wrong with the form submission. Please try again.';
    } else {
      errorMessage.textContent = error.errorData?.detail || error.message || 'There was an error submitting your form. Please try again.';
    }

    const existingError = formElement.querySelector('.form-error-message');
    if (existingError) existingError.remove();

    formElement.appendChild(errorMessage);

    if (error.errorData) {
      Object.entries(error.errorData).forEach(([field, message]) => {
        const formPrefix = formElement.dataset.prefix || '';
        let errorElement = document.getElementById(`error-${formPrefix}${field}`) || document.getElementById(`error-${field}`);
        if (errorElement) errorElement.textContent = message;
      });
    }

    resetFormState();
  }
}